<template>
  <div id="filters">
    <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
      <div
        id="searchBarSection"
        class="atb-flex-shrink"
      >
        <SearchPanel
          page="filters"
          @search-clicked="search"
          @reset-clicked="reset"
        />
      </div>
      <div class="atb-flex-grow">
        <div class="eds-table-wrap atb-w-full atb-h-full">
          <Toolbar
            class-name="atb-bg-white atb-border-t-0 atb-border-x-0 atb-border-b atb-border-solid atb-border-gray-300"
          >
            <template #toolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="filtersListRefreshButton"
                    motif="tertiary"
                    :title="$t('refresh')"
                    aria-label="refresh button"
                    @click="refresh"
                  >
                    <template #icon>
                      <span class="material-icons">loop</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="filtersListCopyButton"
                    motif="tertiary"
                    :title="$t('copy')"
                    aria-label="copy button"
                    @click="openCopyForm"
                  >
                    <template #icon>
                      <span class="material-icons-outlined">file_copy</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="filtersListCreateButton"
                    motif="tertiary"
                    :title="$t('create')"
                    aria-label="create button"
                    @click="openCreateForm"
                  >
                    <template #icon>
                      <span class="material-icons">add_circle_outline</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="filtersListDeleteButton"
                    motif="tertiary"
                    :title="$t('delete')"
                    aria-label="delete button"
                    @click="openDeleteModal"
                  >
                    <template #icon>
                      <span class="material-icons icon">
                        delete_outline_icon
                      </span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
            </template>
          </Toolbar>
          <AgGridVue
            id="filtersListAllFiltersGrid"
            class="ag-theme-eds atb-w-full atb-h-full"
            :column-defs="loadDataSourcesInColumns(columnDefs)"
            :components="gridComponents"
            :row-model-type="'infinite'"
            :pagination-page-size="rowsPerPage"
            :pagination="true"
            :suppress-pagination-panel="true"
            :suppress-cell-selection="true"
            :suppress-row-click-selection="true"
            @grid-ready="onGridReady"
            :row-selection="'multiple'"
            :row-multi-select-with-click="true"
          />
          <Pagination
            v-model="currentPage"
            :page-count="totalPages"
            :total-rows="totalRows"
            :page-size="rowsPerPage"
            @pageClick="changePageAndRefresh"
            @pageSizeChange="changePageSizeAndRefresh"
            class="atb-bg-white"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import SearchPanel from '@/app/features/ams/common/SearchPanel.vue';
  import ClickableCell from '@/app/shared/components/grid-cells/ClickableCell.vue';
  import ExperianLockHeaderColumn from '@/app/shared/components/grid-cells/ExperianLockHeaderColumn.vue';
  import ExperianCodedHeaderColumn from '@/app/shared/components/grid-cells/ExperianCodedHeaderColumn.vue';
  import ExperianWhereUsedHeaderColumn from '@/app/shared/components/grid-cells/ExperianWhereUsedHeaderColumn.vue';
  import WhereUsedCell from '@/app/shared/components/grid-cells/WhereUsedCell.vue';
  import LockCell from '@/app/shared/components/grid-cells/LockCell.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Toolbar from '@atb-ui/eds-vue/src/components/Toolbar/Toolbar.vue';
  import ToolbarSection from '@atb-ui/eds-vue/src/components/Toolbar/ToolbarSection.vue';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';
  import { useFiltersStore } from '@/app/features/ams/filters/filters-store';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { CodeType } from '@/app/shared/enums/code-type.enum';
  import { SearchPanelParams } from '@/app/shared/types/params/search-panel-params.interface';
  import { getTransformedDate } from '@/app/shared/utils/date.util';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import {
    ColumnApi,
    GridApi,
    GridReadyEvent,
    IGetRowsParams,
  } from 'ag-grid-community';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { storeToRefs } from 'pinia';
  import { reactive, Ref, ref, toRefs } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import { Filters } from '@/app/shared/types/filters.interface';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import { usePagination } from '@/app/shared/hooks/use-pagination';
  import { lastUpdatedRenderer } from '@/app/shared/components/grid-cells/cellRenderers';
  import { formatQueryParams } from '@/app/shared/utils/query-params-formatter.util';
  import FilterDataSourceColumnVue from '@/app/shared/components/grid-cells/FilterDataSourceColumn.vue';
  import useCopyForm from '@/app/shared/hooks/use-copy-form';
  import { ViewCodeMetaInformation } from '@/app/shared/types/models/view-code-meta-information.interface';
  import { ListEvents } from '@/app/shared/types/list-events.interface';

  interface ListProps {
    clientId: number;
  }
  interface FiltersListEvents extends ListEvents<ViewCodeMetaInformation> {}

  const emit = defineEmits<FiltersListEvents>();
  const props = defineProps<ListProps>();
  const { t } = useI18n();
  const clientStore = useClientStore();
  const filtersStore = useFiltersStore();
  const { requestParams, tabs, filters, massDeleteDependencies } =
    storeToRefs(filtersStore);
  const { selectedClient } = storeToRefs(clientStore);
  const { clientId } = toRefs(props);
  const alertStore = useAlertStore();
  const gridApi = ref(null) as Ref<GridApi | null>;
  const columnApi = reactive({}) as Ref<ColumnApi | null>;
  const {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    changePage,
    changePageSize,
    calculatePagination,
  } = usePagination(
    filters.value.numberOfElements,
    filters.value.totalElements,
    gridApi
  );
  const { openCopyForm } = useCopyForm<
    ListEvents<ViewCodeMetaInformation>,
    ViewCodeMetaInformation
  >(gridApi, 'filters', emit);

  const changePageAndRefresh = (page: number) => {
    changePage(page);
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const changePageSizeAndRefresh = (newPageSize: number | string) => {
    changePageSize(newPageSize);
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const lockFilterOrDs = async (
    id: number,
    isAutoLock: boolean,
    dataSourceName?: string
  ) => {
    const notificationMessage = dataSourceName
      ? 'dataSourceLocked'
      : 'filterLocked';
    await filtersStore
      .lockFilterOrDs({
        clientId: clientId.value,
        filterId: id,
        isAutoLock: isAutoLock,
        dataSourceName: dataSourceName,
      })
      .then(() => {
        displayNotificationMessage(notificationMessage);
      })
      .catch((error) => {
        alertStore.showNotification(
          error.errors[0].message,
          'Filters',
          Motif.ERROR
        );
      });

    refresh();
  };

  const unlockFilterOrDs = async (
    id: number,
    isAutoLock: boolean,
    dataSourceName?: string
  ) => {
    const notificationMessage = dataSourceName
      ? 'dataSourceUnlocked'
      : 'filterUnlocked';
    await filtersStore
      .unlockFilterOrDs({
        clientId: clientId.value,
        filterId: id,
        isAutoLock: isAutoLock,
        dataSourceName: dataSourceName,
      })
      .then(() => {
        displayNotificationMessage(notificationMessage);
      })
      .catch((error) => {
        if (!isAutoLock) {
          alertStore.showNotification(
            error.errors[0].message,
            'Filters',
            Motif.ERROR
          );
        }
      });

    refresh();
  };

  const displayNotificationMessage = (notificationMessage: string) => {
    alertStore.showNotification(
      t(notificationMessage),
      t('filters'),
      Motif.SUCCESS
    );
  };

  let areFiltersOpen = () => {
    return filtersStore.tabs.length > 1;
  };

  const lockAllFilters = async () => {
    await filtersStore.lockAllFilters(clientId.value);
    alertStore.showNotification(
      t('allFiltersLocked'),
      t('filters'),
      Motif.SUCCESS
    );
  };

  const unlockAllFilters = async () => {
    await filtersStore.unlockAllFilters(clientId.value);
    alertStore.showNotification(
      t('allFiltersUnlocked'),
      t('filters'),
      Motif.SUCCESS
    );
  };

  const lockAllDataSources = async (dataSourceName: string) => {
    await filtersStore.lockAllDataSources(clientId.value, dataSourceName);
    alertStore.showNotification(
      t('allDataSourcesLocked'),
      t('filters'),
      Motif.SUCCESS
    );
  };

  const unlockAllDataSources = async (dataSourceName: string) => {
    await filtersStore.unlockAllDataSources(clientId.value, dataSourceName);
    alertStore.showNotification(
      t('allDataSourcesUnlocked'),
      t('filters'),
      Motif.SUCCESS
    );
  };

  const addFilterToTabs = async (filterId: number, filterName: string) => {
    /**flow for auto locking,
     * todo: if lock request failed?,
     * todo: readonly access of subtabs(if locked)*/
    try {
      await filtersStore.lockFilterOrDs({
        clientId: clientId.value,
        filterId: filterId,
        isAutoLock: true,
      });
    } catch (error) {
      displayError(error);
    }
    emit('createNewTab', {
      id: filterId,
      name: filterName,
      dirtyTab: new Map<string, boolean>(),
      clientId: clientId.value,
      isNew: false,
    });
    gridApi.value?.refreshCells();
  };

  const openInUseEntities = (id: number) => {
    filtersStore.fetchWhereUsedDetails(clientId.value, id).catch((error) => {
      displayError(error);
    });
  };

  const columnDefs = [
    {
      field: 'id',
      headerName: '',
      suppressMovable: true,
      width: 50,
      minWidth: 50,
      sortable: false,
      checkboxSelection: true,
      valueFormatter: () => '',
    },
    {
      field: 'shortName',
      headerName: t('filterName'),
      suppressMovable: true,
      width: 250,
      minWidth: 250,
      sortable: true,
      cellRendererFramework: ClickableCell,
      cellRendererParams: {
        onClick: addFilterToTabs,
      },
    },
    {
      field: 'isInUse',
      headerName: '',
      suppressMovable: true,
      width: 56,
      minWidth: 56,
      sortable: false,
      headerComponentFramework: ExperianWhereUsedHeaderColumn,
      cellRendererFramework: WhereUsedCell,
      cellRendererParams: {
        entity: 'filter',
        whereUsedInfo: openInUseEntities,
      },
    },
    {
      field: 'versionControls',
      headerName: '',
      suppressMovable: true,
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      sortable: false,
      headerComponentFramework: ExperianLockHeaderColumn,
      headerComponentParams: {
        entity: 'filter',
        areEntitiesOpen: areFiltersOpen,
        lockAll: lockAllFilters,
        unlockAll: unlockAllFilters,
      },
      cellRendererFramework: LockCell,
      cellRendererParams: {
        lock: lockFilterOrDs,
        unlock: unlockFilterOrDs,
        // TODO: this may have been replaced with a computed value
        getAutoLockStatusById: (id: number) =>
          filtersStore.tabs.find((tab) => tab.id === id),
      },
    },
    {
      field: 'lastUpdated',
      headerName: t('lastUpdated'),
      suppressMovable: true,
      width: 333,
      minWidth: 333,
      sortable: true,
      cellRenderer: 'lastUpdatedRenderer',
    },
  ];

  const gridComponents = {
    lastUpdatedRenderer,
  };

  const displayError = (error: any) => {
    alertStore.showNotification(
      error.errors[0].message,
      t('filters'),
      Motif.ERROR
    );
  };

  const loadDataSourcesInColumns = (columns: Array<any>) => {
    const dataSourcesList: Array<Datasource> = selectedClient.value.dataSources;
    dataSourcesList.forEach((ds) => {
      columns.splice(4, 0, {
        field: 'versionControls',
        suppressMovable: true,
        width: 150,
        minWidth: 150,
        maxWidth: 250,
        headerComponentFramework: ExperianCodedHeaderColumn,
        headerComponentParams: {
          dataSourceName: ds.name,
          lockAll: lockAllDataSources,
          unlockAll: unlockAllDataSources,
        },
        cellClass: 'atb-flex atb-items-center',
        cellRendererFramework: FilterDataSourceColumnVue,
        cellRendererParams: {
          dataSourceName: ds.name,
          lockDataSource: lockFilterOrDs,
          unlockDataSource: unlockFilterOrDs,
        },
      });
    });
    return columns;
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
    gridApi.value.setDatasource(serverSideDatasource());
    gridApi.value.sizeColumnsToFit();
  };

  const serverSideDatasource = () => {
    return {
      getRows: async ({
        sortModel,
        successCallback,
        failCallback,
      }: IGetRowsParams) => {
        try {
          gridApi.value?.showLoadingOverlay();
          await filtersStore.fetchFilters(
            clientId.value,
            formatQueryParams(sortModel, {
              ...requestParams.value,
              page: currentPage.value > 0 ? currentPage.value - 1 : 0,
              size: rowsPerPage.value,
            })
          );
          if (filters.value.numberOfElements > 0) {
            gridApi.value?.hideOverlay();
          } else {
            gridApi.value?.showNoRowsOverlay();
          }
          calculatePagination(filters.value.totalElements);
          successCallback(
            filters.value.content || [],
            filters.value.numberOfElements || 0
          );
        } catch (error) {
          gridApi.value?.showNoRowsOverlay();
          failCallback();
          console.error('error fetching overview list');
        }
      },
    };
  };

  const refresh = (): void => {
    gridApi.value?.purgeInfiniteCache();
  };

  const reset = (): void => {
    requestParams.value = {};
    filtersStore.updateRequestParams({
      codeType: CodeType.FILTER,
    });
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const search = (searchPanelParams: SearchPanelParams) => {
    filtersStore.updateRequestParams({
      name: searchPanelParams.nameSearchField
        ? searchPanelParams.nameSearchField
        : undefined,
      format: searchPanelParams.formatSelected
        ? searchPanelParams.formatSelected
        : undefined,
      stagg: searchPanelParams.experianSelected
        ? searchPanelParams.experianSelected
        : undefined,
      createdFrom: searchPanelParams.createdFromDate
        ? getTransformedDate(searchPanelParams.createdFromDate)
        : undefined,
      createdTo: searchPanelParams.createdToDate
        ? getTransformedDate(searchPanelParams.createdToDate, true)
        : undefined,
      updatedFrom: searchPanelParams.updatedFromDate
        ? getTransformedDate(searchPanelParams.updatedFromDate)
        : undefined,
      updatedTo: searchPanelParams.updatedToDate
        ? getTransformedDate(searchPanelParams.updatedToDate, true)
        : undefined,
      system: searchPanelParams.systemSelected
        ? searchPanelParams.systemSelected
        : undefined,
      updatedBy: searchPanelParams.updatedUserSelected
        ? searchPanelParams.updatedUserSelected
        : undefined,
      codeType: CodeType.FILTER,
    });
    changePageAndRefresh(1);
  };

  const openCreateForm = () => {
    emit('create');
  };

  const isRowSelected = () => {
    return (
      gridApi.value?.getSelectedRows().map((filter) => filter.id).length !== 0
    );
  };

  const isFilterOpen = () => {
    const selectedFilters: Array<number> | undefined = gridApi.value
      ?.getSelectedRows()
      .map((filter: Filters) => filter.id);
    return tabs.value
      .map((tab: TabHeader) => {
        return selectedFilters?.find((id) => id === tab.id);
      })
      .find((id?: number) => typeof id !== 'undefined');
  };

  const openDeleteModal = async () => {
    if (!isRowSelected()) {
      return alertStore.showNotification(
        t('filtersNotSelected'),
        t('filters'),
        Motif.ERROR
      );
    }
    if (isFilterOpen()) {
      return alertStore.showNotification(
        t('deleteError'),
        t('filters'),
        Motif.ERROR
      );
    }
    const selectedFilters: Array<number> | undefined = gridApi.value
      ?.getSelectedRows()
      .map((filter: Filters) => filter.id);
    await filtersStore
      .retrieveCyclicDependencies(clientId.value, selectedFilters)
      .catch((error) => {
        alertStore.showNotification(
          error.errors[0].message,
          'Filters',
          Motif.ERROR
        );
      });
    if (massDeleteDependencies.value?.length > 0) {
      emit('showDependency');
    } else {
      emit('delete');
    }
  };

  defineExpose({
    refresh,
    gridApi,
  });
</script>
<style scoped>
  .icon {
    width: 20px;
  }
</style>
